import {
	Anchor,
	ArrowRightLeft,
	Award,
	Bolt,
	Book,
	BookOpen,
	Brain,
	Briefcase,
	Calendar,
	CalendarCheck,
	Check,
	CircleHelp,
	Clock,
	Cloud,
	Compass,
	Crown,
	DoorClosed,
	DoorOpen,
	Eye,
	EyeOff,
	Flame,
	Frown,
	Gift,
	Globe,
	GraduationCap,
	HandHeart,
	HandHelping,
	Handshake,
	Heart,
	HeartCrack,
	HeartHandshake,
	Hourglass,
	Image,
	Leaf,
	Lightbulb,
	Link,
	Lock,
	Map,
	MapPin,
	Medal,
	Megaphone,
	Meh,
	MessageCircle,
	MessageSquare,
	Palette,
	PartyPopper,
	Puzzle,
	Rainbow,
	RefreshCcw,
	RefreshCw,
	Rocket,
	Ruler,
	Scale,
	Search,
	Shield,
	ShieldHalf,
	Smile,
	Sparkles,
	Sprout,
	Star,
	Sun,
	Target,
	ThumbsUp,
	Trash2,
	TrendingDown,
	TrendingUp,
	Trophy,
	Unlock,
	User,
	Users,
	UsersRound,
	Wrench,
	X,
	Zap,
	ZapOff,
} from 'lucide-static';
import { pickableIconNames } from 'shared/modelTypes/jackpot';
import { Icon } from '../Icon';

const iconByName: Record<string, string> = {
	Palette,
	HandHeart,
	Hourglass,
	RefreshCcw,
	RefreshCw,
	Shield,
	PartyPopper,
	Rainbow,
	Lock,
	Unlock,
	BookOpen,
	Book,
	Smile,
	Frown,
	Meh,
	Target,
	Puzzle,
	Scale,
	Handshake,
	HandHelping,
	HeartHandshake,
	Heart,
	HeartCrack,
	Search,
	MessageCircle,
	MessageSquare,
	Megaphone,
	Sparkles,
	Star,
	TrendingUp,
	TrendingDown,
	Wrench,
	Globe,
	Zap,
	ZapOff,
	Ruler,
	Lightbulb,
	Brain,
	Sun,
	Cloud,
	Flame,
	Users,
	User,
	UsersRound,
	Map,
	MapPin,
	Compass,
	Rocket,
	Check,
	Link,
	Sprout,
	Leaf,
	Award,
	Medal,
	Trophy,
	Crown,
	Gift,
	Clock,
	Calendar,
	CalendarCheck,
	Anchor,
	ThumbsUp,
	ShieldHalf,
	Eye,
	EyeOff,
	DoorOpen,
	DoorClosed,
	ArrowRightLeft,
	CircleHelp,
	Bolt,
	Trash2,
	GraduationCap,
	Briefcase,
};

export function iconSvgByName(name: string | undefined): string | undefined {
	if (!name) return undefined;
	return iconByName[name];
}

export function IconPicker({ name, value }: { name: string; value?: string }) {
	const hiddenInput: HTMLInputElement = (
		<input type="hidden" name={name} value={value ?? ''} />
	) as HTMLInputElement;

	const previewSlot: HTMLElement = (<span class="grid size-6 place-items-center text-gray-400"></span>) as HTMLElement;

	function renderPreview() {
		const cur = hiddenInput.value;
		previewSlot.innerHTML = '';
		if (cur && iconByName[cur]) {
			const svg = document.createRange().createContextualFragment(iconByName[cur]).children[0] as SVGElement;
			svg.setAttribute('class', 'size-6 text-gray-700');
			previewSlot.appendChild(svg);
		} else {
			const svg = document.createRange().createContextualFragment(Image).children[0] as SVGElement;
			svg.setAttribute('class', 'size-6 text-gray-300');
			previewSlot.appendChild(svg);
		}
	}
	renderPreview();

	const popup: HTMLElement = (
		<div
			class="absolute z-30 mt-1 hidden max-h-72 w-72 overflow-auto rounded-md border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800"
			data-icon-popup
		>
			<div class="grid grid-cols-8 gap-1">
				<button
					type="button"
					title="Geen icoon"
					class="grid size-8 place-items-center rounded border border-transparent hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
					on:click={() => {
						hiddenInput.value = '';
						renderPreview();
						popup.classList.add('hidden');
					}}
				>
					<Icon icon={X} class="size-4 text-gray-400" />
				</button>
				{pickableIconNames.map((iconName) => {
					const svg = iconByName[iconName];
					if (!svg) return null as unknown as HTMLElement;
					return (
						<button
							type="button"
							title={iconName}
							class="grid size-8 place-items-center rounded border border-transparent hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
							on:click={() => {
								hiddenInput.value = iconName;
								renderPreview();
								popup.classList.add('hidden');
							}}
						>
							<Icon icon={svg} class="size-4 text-gray-700 dark:text-gray-200" />
						</button>
					);
				})}
			</div>
		</div>
	);

	const trigger: HTMLElement = (
		<button
			type="button"
			class="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800"
			on:click={(e: MouseEvent) => {
				e.stopPropagation();
				popup.classList.toggle('hidden');
			}}
		>
			{previewSlot}
		</button>
	);

	const view: HTMLElement = (
		<div class="relative inline-block">
			{hiddenInput}
			{trigger}
			{popup}
		</div>
	);

	// Close popup when clicking outside.
	document.addEventListener('click', (e) => {
		if (!view.contains(e.target as Node)) popup.classList.add('hidden');
	});

	return view;
}
